import {
  ExamUsageStatus,
  useUpdateExamUsageOfQSetsMutation,
  GetPublishedQSetsDocument,
} from '@exam-management/graphql';
import { useNavigate } from '@exam-management/router';
import { useSnackbar } from '@shared/snackbar';
import { type PropsWithChildren } from '@shared/utils';
import { createContext, useCallback, useMemo, useState } from 'react';

import { QSetMarkExamUsageAsInactiveModal, QSetMarkExamUsageAsReadyModal } from '../../components';
import { QSetDisablementModal, QSetSimilarityCheckModal } from '../../containers';
import { useQSetSimilarityCheck } from '../../hooks';
import { type CheckQSetDuplicationRateFormValues } from '../../types';

enum PublishedQSetListModalAction {
  CHECK_PUBLISHED = 'CHECK_PUBLISHED',
  MARK_AS_READY = 'MARK_AS_READY',
  MARK_AS_INACTIVE = 'MARK_AS_INACTIVE',
  DISABLED = 'DISABLED',
}

interface PublishedQSetListModalProviderProps extends PropsWithChildren {
  courseId: string;
}

interface PublishedQSetListContextData {
  selectedQSetIds: string[];
  onSelectQSet: (qSetIds: string[], status: (ExamUsageStatus | null)[]) => void;
  examUsageStatuses: (ExamUsageStatus | null)[];
  openMarkAsReadyModal: () => void;
  openMarkAsInactiveModal: () => void;
  openDisableModal: () => void;
  openCheckDuplicatePublishedModal: () => void;
}

export const PublishedQSetListContext = createContext<PublishedQSetListContextData | undefined>(
  undefined,
);

export default function PublishedQSetListModalProvider(
  props: Readonly<PublishedQSetListModalProviderProps>,
) {
  const { children, courseId } = props;
  const [selectedQSetIds, setSelectedQSetIds] = useState<string[]>([]);
  const [examUsageStatuses, setExamUsageStatuses] = useState<Array<ExamUsageStatus | null>>([]);
  const [action, setAction] = useState<PublishedQSetListModalAction | null>(null);
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const isMarkAsReadyModalOpen = action === PublishedQSetListModalAction.MARK_AS_READY;
  const isMarkAsInactiveModalOpen = action === PublishedQSetListModalAction.MARK_AS_INACTIVE;
  const isCheckDuplicatePublishedModalOpen =
    action === PublishedQSetListModalAction.CHECK_PUBLISHED;

  const [execute] = useQSetSimilarityCheck();

  const handleModalClose = useCallback(() => {
    setAction(null);
  }, [setAction]);

  const clearData = useCallback(() => {
    setSelectedQSetIds([]);
    setExamUsageStatuses([]);
  }, [setSelectedQSetIds, setExamUsageStatuses]);

  const isDisabledModalOpen = action === PublishedQSetListModalAction.DISABLED;

  const handleSelectQSetIds = useCallback(
    (qSetIds: string[], status: (ExamUsageStatus | null)[]) => {
      setSelectedQSetIds(qSetIds);
      setExamUsageStatuses(status);
    },
    [setSelectedQSetIds, setExamUsageStatuses],
  );

  const [updateExamUsageOfQSets, { loading: isUpdatingExamUsageOfQSets }] =
    useUpdateExamUsageOfQSetsMutation({
      refetchQueries: [GetPublishedQSetsDocument],
    });

  const submitToMarkAsReadyModal = useCallback(
    async (qSetIds: string[]) => {
      await updateExamUsageOfQSets({
        variables: {
          input: {
            qSetIds,
            toExamUsageStatus: ExamUsageStatus.Ready,
          },
        },
        onCompleted: () => {
          handleModalClose();
          clearData();
          showSnackbar({ message: 'Marked as Ready successfully', variant: 'success' });
        },
        onError: () => {
          showSnackbar({ message: 'Mark as Ready failed. Please try again', variant: 'error' });
        },
      });
    },
    [updateExamUsageOfQSets],
  );

  const submitToMarkAsInactiveModal = useCallback(
    async (qSetIds: string[]) => {
      await updateExamUsageOfQSets({
        variables: {
          input: {
            qSetIds,
            toExamUsageStatus: ExamUsageStatus.Inactive,
          },
        },
        onCompleted: () => {
          handleModalClose();
          clearData();
          showSnackbar({ message: 'Marked as Inactive successfully', variant: 'success' });
        },
        onError: () => {
          showSnackbar({ message: 'Mark as Inactive failed. Please try again', variant: 'error' });
        },
      });
    },
    [updateExamUsageOfQSets],
  );

  const submitToCheckDuplicatePublishedModal = useCallback(
    async (formData: CheckQSetDuplicationRateFormValues) => {
      await execute(selectedQSetIds, {
        onCompleted: (data, draftQSets, publishedQSets) => {
          navigate('/exam-courses/:courseId/qsets/similarity-check/results', {
            params: {
              courseId,
            },
            state: {
              id: data.id,
              checkType: 'PUBLISHED_TO_PUBLISHED',
              courseId,
              duplicationRate: formData.duplicationRate,
              similarityResult: data.qSetSimilarityResults,
              draftQSets,
              publishedQSets,
            },
          });
          setSelectedQSetIds([]);
        },
        onError: () => {
          showSnackbar({
            message: 'Unable to check for duplicates. Please try again.',
            variant: 'error',
          });
        },
      });
    },
    [navigate, courseId, selectedQSetIds],
  );

  const contextValue = useMemo(
    () => ({
      selectedQSetIds,
      onSelectQSet: handleSelectQSetIds,
      examUsageStatuses,
      openDisableModal: () => setAction(PublishedQSetListModalAction.DISABLED),
      openMarkAsReadyModal: () => setAction(PublishedQSetListModalAction.MARK_AS_READY),
      openMarkAsInactiveModal: () => setAction(PublishedQSetListModalAction.MARK_AS_INACTIVE),
      openCheckDuplicatePublishedModal: () =>
        setAction(PublishedQSetListModalAction.CHECK_PUBLISHED),
    }),
    [
      selectedQSetIds,
      handleSelectQSetIds,
      examUsageStatuses,
      isMarkAsReadyModalOpen,
      isMarkAsInactiveModalOpen,
      isCheckDuplicatePublishedModalOpen,
    ],
  );

  return (
    <PublishedQSetListContext.Provider value={contextValue}>
      {children}
      {isMarkAsReadyModalOpen ? (
        <QSetMarkExamUsageAsReadyModal
          open={isMarkAsReadyModalOpen}
          onClose={handleModalClose}
          handleSubmit={submitToMarkAsReadyModal}
          selectedQSets={selectedQSetIds}
          loading={isUpdatingExamUsageOfQSets}
        />
      ) : null}
      {isMarkAsInactiveModalOpen ? (
        <QSetMarkExamUsageAsInactiveModal
          open={isMarkAsInactiveModalOpen}
          onClose={handleModalClose}
          handleSubmit={submitToMarkAsInactiveModal}
          selectedQSets={selectedQSetIds}
          loading={isUpdatingExamUsageOfQSets}
        />
      ) : null}
      {isDisabledModalOpen ? (
        <QSetDisablementModal
          open={isDisabledModalOpen}
          onClose={handleModalClose}
          qSetIds={selectedQSetIds}
        />
      ) : null}
      {isCheckDuplicatePublishedModalOpen ? (
        <QSetSimilarityCheckModal
          open={isCheckDuplicatePublishedModalOpen}
          onClose={handleModalClose}
          onFormSubmit={submitToCheckDuplicatePublishedModal}
        />
      ) : null}
    </PublishedQSetListContext.Provider>
  );
}
